import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Drawer,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Switch,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import LoadingCard from '../common/LoadingCard';

const buildSnapshot = (subAccounts, dispatchers) => {
    const normalizeSubAccounts = subAccounts
        .filter((entry) => entry.can_view)
        .map((entry) => ({ id: entry.id, can_edit: !!entry.can_edit }))
        .sort((a, b) => a.id - b.id);

    const normalizeDispatchers = dispatchers
        .map((entry) => ({
            dispatcher_id: entry.dispatcher_id,
            context_user_id: entry.context_user_id,
            can_edit: !!entry.can_edit,
        }))
        .sort((a, b) => a.dispatcher_id - b.dispatcher_id);

    return {
        subAccounts: normalizeSubAccounts,
        dispatchers: normalizeDispatchers,
    };
};

const snapshotEquals = (left, right) => {
    return JSON.stringify(left) === JSON.stringify(right);
};

const buildUrlWithGroup = (baseUrl, groupName) => {
    try {
        const url = new URL(baseUrl, window.location.origin);
        url.searchParams.set('group', groupName);
        return url.toString();
    } catch (error) {
        console.error('Invalid group access URL', error);
        return baseUrl + (baseUrl.includes('?') ? '&' : '?') + `group=${encodeURIComponent(groupName)}`;
    }
};

const GroupAccessDrawer = ({ infoUrl, updateUrl }) => {
    const [open, setOpen] = useState(false);
    const [groupName, setGroupName] = useState('');
    const [itemCount, setItemCount] = useState(0);

    const [availableSubAccounts, setAvailableSubAccounts] = useState([]);
    const [availableDispatchers, setAvailableDispatchers] = useState([]);
    const [availableContexts, setAvailableContexts] = useState([]);

    const [subAccountRows, setSubAccountRows] = useState([]);
    const [dispatcherRows, setDispatcherRows] = useState([]);

    const [initialSnapshot, setInitialSnapshot] = useState(buildSnapshot([], []));

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const unassignedDispatchers = useMemo(() => {
        const assignedIds = new Set(dispatcherRows.map((entry) => entry.dispatcher_id));
        return availableDispatchers.filter((dispatcher) => !assignedIds.has(dispatcher.id));
    }, [availableDispatchers, dispatcherRows]);

    const dispatcherMetaMap = useMemo(() => {
        const map = new Map();
        availableDispatchers.forEach((dispatcher) => {
            map.set(dispatcher.id, dispatcher);
        });
        return map;
    }, [availableDispatchers]);

    const hasChanges = useMemo(() => {
        const currentSnapshot = buildSnapshot(subAccountRows, dispatcherRows);
        return !snapshotEquals(currentSnapshot, initialSnapshot);
    }, [dispatcherRows, subAccountRows, initialSnapshot]);

    const resetState = () => {
        setGroupName('');
        setItemCount(0);
        setAvailableSubAccounts([]);
        setAvailableDispatchers([]);
        setAvailableContexts([]);
        setSubAccountRows([]);
        setDispatcherRows([]);
        setInitialSnapshot(buildSnapshot([], []));
        setError(null);
        setSuccess(null);
    };

    const applyPayload = (payload, requestedGroup) => {
        const group = payload.group ?? {};
        const groupLabel = group.name ?? requestedGroup;
        setGroupName(groupLabel);
        setItemCount(group.item_count ?? 0);

        const subs = payload.available_sub_accounts ?? [];
        const subAssignments = payload.sub_account_assignments ?? [];
        const subAssignmentMap = new Map(subAssignments.map((entry) => [entry.user_id, entry]));

        const mergedSubAccounts = subs.map((entry) => ({
            id: entry.id,
            name: entry.name ?? `User #${entry.id}`,
            can_view: subAssignmentMap.get(entry.id)?.can_view ?? false,
            can_edit: subAssignmentMap.get(entry.id)?.can_edit ?? false,
        }));

        subAssignments.forEach((entry) => {
            if (!mergedSubAccounts.some((row) => row.id === entry.user_id)) {
                mergedSubAccounts.push({
                    id: entry.user_id,
                    name: entry.name ?? `User #${entry.user_id}`,
                    can_view: entry.can_view ?? true,
                    can_edit: entry.can_edit ?? false,
                });
            }
        });

        mergedSubAccounts.sort((a, b) => a.name.localeCompare(b.name));

        setAvailableSubAccounts(subs.map((entry) => ({ id: entry.id, name: entry.name ?? `User #${entry.id}` })));
        setSubAccountRows(mergedSubAccounts);

        const contexts = (payload.available_contexts ?? []).map((context) => ({
            id: Number(context.id),
            name: context.name ?? `Context #${context.id}`,
            type: context.type ?? 'context',
            disabled: !!context.disabled,
        }));
        const freshContextMap = new Map(contexts.map((context) => [context.id, context]));

        setAvailableContexts(contexts);

        const dispatchers = payload.available_dispatchers ?? [];
        const dispatcherRoleMap = new Map(dispatchers.map((entry) => [entry.id, entry.role_id]));
        const dispatcherOwnerMap = new Map(dispatchers.map((entry) => [entry.id, entry.owner_name]));
        const dispatcherAssignments = (payload.dispatcher_assignments ?? []).map((entry) => ({
            dispatcher_id: entry.dispatcher_id,
            name: entry.name ?? `Dispatcher #${entry.dispatcher_id}`,
            context_user_id: Number(entry.context_user_id),
            context_name: entry.owner_name
                ?? dispatcherOwnerMap.get(entry.dispatcher_id)
                ?? freshContextMap.get(Number(entry.context_user_id))?.name
                ?? `Account #${entry.context_user_id}`,
            can_edit: false,
            role_id: entry.role_id ?? dispatcherRoleMap.get(entry.dispatcher_id) ?? null,
            owner_name: entry.owner_name ?? dispatcherOwnerMap.get(entry.dispatcher_id) ?? null,
        }));

        dispatcherAssignments.sort((a, b) => a.name.localeCompare(b.name));

        setAvailableDispatchers(dispatchers.map((entry) => ({
            id: entry.id,
            name: entry.name ?? `Dispatcher #${entry.id}`,
            role_id: entry.role_id,
            owner_id: entry.owner_id,
            owner_name: entry.owner_name ?? null,
        })));
        setDispatcherRows(dispatcherAssignments);

        setInitialSnapshot(buildSnapshot(mergedSubAccounts, dispatcherAssignments));
    };

    const fetchGroupData = (requestedGroup) => {
        if (!requestedGroup) {
            setError('Group name is required');
            return;
        }

        setLoading(true);
        setError(null);
        setSuccess(null);

        const requestUrl = buildUrlWithGroup(infoUrl, requestedGroup);

        fetch(requestUrl, {
            headers: {
                Accept: 'application/json',
            },
        })
            .then(async (response) => {
                if (!response.ok) {
                    const data = await response.json().catch(() => ({}));
                    throw new Error(data.error || 'Failed to load group access data');
                }
                return response.json();
            })
            .then((data) => {
                applyPayload(data, requestedGroup);
            })
            .catch((loadError) => {
                console.error('Error loading group access data', loadError);
                setError(loadError.message || 'Failed to load group access data');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const handleToggleSubAccount = (userId) => {
        setSubAccountRows((prev) => prev.map((entry) => {
            if (entry.id !== userId) {
                return entry;
            }

            const nextView = !entry.can_view;
            return {
                ...entry,
                can_view: nextView,
                can_edit: nextView ? entry.can_edit : false,
            };
        }));
    };

    const handleToggleSubAccountEdit = (userId) => {
        setSubAccountRows((prev) => prev.map((entry) => {
            if (entry.id !== userId) {
                return entry;
            }

            if (!entry.can_view) {
                return entry;
            }

            return {
                ...entry,
                can_edit: !entry.can_edit,
            };
        }));
    };

    const handleAddDispatcher = (dispatcherId) => {
        if (!dispatcherId) {
            return;
        }

        const dispatcher = availableDispatchers.find((entry) => entry.id === dispatcherId);
        if (!dispatcher) {
            return;
        }

        if (dispatcherRows.some((entry) => entry.dispatcher_id === dispatcherId)) {
            return;
        }

        const contextId = dispatcher.owner_id;
        if (!contextId) {
            setError('Dispatcher is missing an owner account.');
            return;
        }

        const contextName = dispatcher.owner_name ?? `Account #${contextId}`;

        setDispatcherRows((prev) => {
            const next = [
                ...prev,
                {
                    dispatcher_id: dispatcher.id,
                    name: dispatcher.name ?? `Dispatcher #${dispatcher.id}`,
                    context_user_id: contextId,
                    context_name: contextName,
                    can_edit: false,
                    role_id: dispatcher.role_id,
                    owner_name: dispatcher.owner_name ?? null,
                },
            ];

            next.sort((a, b) => a.name.localeCompare(b.name));
            return next;
        });
    };

    const handleRemoveDispatcher = (dispatcherId) => {
        setDispatcherRows((prev) => prev.filter((entry) => entry.dispatcher_id !== dispatcherId));
    };

    const performSave = () => {
        if (!groupName) {
            setError('Group name is required');
            return;
        }

        setSaving(true);
        setError(null);
        setSuccess(null);

        const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

        const payload = {
            group: groupName,
            sub_accounts: subAccountRows
                .filter((entry) => entry.can_view)
                .map((entry) => ({
                    user_id: entry.id,
                    can_view: true,
                    can_edit: !!entry.can_edit,
                })),
            dispatchers: dispatcherRows.map((entry) => ({
                dispatcher_id: entry.dispatcher_id,
                context_user_id: entry.context_user_id,
                can_view: true,
                can_edit: !!entry.can_edit,
            })),
        };

        fetch(updateUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-TOKEN': csrfToken,
            },
            body: JSON.stringify(payload),
        })
            .then(async (response) => {
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                    throw new Error(data.error || 'Failed to update group access');
                }
                setSuccess(data.message || 'Group access updated successfully');
                applyPayload(data, groupName);
            })
            .catch((saveError) => {
                console.error('Error updating group access', saveError);
                setError(saveError.message || 'Failed to update group access');
            })
            .finally(() => {
                setSaving(false);
            });
    };

    const handleClose = () => {
        setOpen(false);
        resetState();
    };

    useEffect(() => {
        const handler = (event) => {
            const requestedGroup = event.detail?.groupName ?? '';
            setOpen(true);
            fetchGroupData(requestedGroup);
        };

        window.addEventListener('groups:accessSettings', handler);
        return () => {
            window.removeEventListener('groups:accessSettings', handler);
        };
    }, []);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={handleClose}
            PaperProps={{ sx: { width: 520 } }}
        >
            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', height: '100%' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Box>
                        <Typography variant="h6">Group Access</Typography>
                        {groupName && (
                            <Typography variant="caption" color="textSecondary">
                                {groupName} · {itemCount} item{itemCount === 1 ? '' : 's'}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={handleClose}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                {loading && <LoadingCard />}

                {!loading && error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {!loading && success && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {success}
                    </Alert>
                )}

                {!loading && !error && (
                    <Box sx={{ flex: 1, overflowY: 'auto', pr: 1 }}>
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Sub Accounts</Typography>
                            {subAccountRows.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                    No sub accounts available for this owner.
                                </Typography>
                            ) : (
                                <FormGroup>
                                    {subAccountRows.map((entry) => (
                                        <Box
                                            key={entry.id}
                                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
                                        >
                                            <FormControlLabel
                                                control={(
                                                    <Checkbox
                                                        checked={entry.can_view}
                                                        onChange={() => handleToggleSubAccount(entry.id)}
                                                    />
                                                )}
                                                label={entry.name}
                                            />
                                            <FormControlLabel
                                                control={(
                                                    <Switch
                                                        checked={entry.can_edit}
                                                        onChange={() => handleToggleSubAccountEdit(entry.id)}
                                                        disabled={!entry.can_view}
                                                    />
                                                )}
                                                label="Can edit"
                                            />
                                        </Box>
                                    ))}
                                </FormGroup>
                            )}
                        </Box>

                        <Box>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>Dispatchers</Typography>
                            <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                Dispatchers and supervisor dispatchers have view-only access here. Use the context to choose
                                which account they act on behalf of.
                            </Typography>

                            <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                                <InputLabel id="group-access-add-dispatcher-label">Add dispatcher</InputLabel>
                                <Select
                                    labelId="group-access-add-dispatcher-label"
                                    label="Add dispatcher"
                                    value=""
                                    onChange={(event) => {
                                        const value = Number(event.target.value);
                                        if (value) {
                                            handleAddDispatcher(value);
                                        }
                                    }}
                                    disabled={unassignedDispatchers.length === 0}
                                >
                                    <MenuItem value="">
                                        {unassignedDispatchers.length === 0 ? 'No dispatchers available' : 'Select dispatcher'}
                                    </MenuItem>
                                    {unassignedDispatchers.map((dispatcher) => (
                                        <MenuItem key={dispatcher.id} value={dispatcher.id}>
                                            {dispatcher.name}
                                            {dispatcher.owner_name ? ` · ${dispatcher.owner_name}` : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {dispatcherRows.length === 0 ? (
                                <Typography variant="body2" color="textSecondary">
                                    No dispatchers assigned to this group.
                                </Typography>
                            ) : (
                                dispatcherRows.map((entry) => {
                                    const dispatcherMeta = dispatcherMetaMap.get(entry.dispatcher_id);
                                    const roleLabel = dispatcherMeta?.role_id === 7 ? 'Supervisor Dispatcher' : 'Dispatcher';
                                    const ownerLabel = dispatcherMeta?.owner_name ? `Owner: ${dispatcherMeta.owner_name}` : null;

                                    return (
                                    <Box
                                        key={entry.dispatcher_id}
                                        sx={{
                                            display: 'grid',
                                            gridTemplateColumns: '1fr auto',
                                            gap: 1,
                                            alignItems: 'center',
                                            mb: 2,
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                {entry.name}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                {roleLabel}
                                                {ownerLabel ? ` · ${ownerLabel}` : ''}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Context: {entry.context_name ?? 'Owner'}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                Update the dispatcher’s owner to change their context.
                                            </Typography>
                                        </Box>

                                        <IconButton onClick={() => handleRemoveDispatcher(entry.dispatcher_id)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                );
                                })
                            )}
                        </Box>
                    </Box>
                )}

                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between' }}>
                    <Button onClick={handleClose} disabled={saving}>
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={performSave}
                        disabled={saving || !hasChanges}
                    >
                        {saving ? 'Saving…' : 'Save changes'}
                    </Button>
                </Box>
            </Box>
        </Drawer>
    );
};

export default GroupAccessDrawer;
